import { useState } from 'react'
import api from '../services/api'
import DatePicker from 'react-datepicker'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import 'react-datepicker/dist/react-datepicker.css'
import './SaldoHistoricoPage.css'

export default function SaldoHistoricoPage() {
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [expanded, setExpanded] = useState({})

  const fetchBalanceHistory = async () => {
    if (!fromDate || !toDate) {
      setError('Seleccione ambas fechas')
      return
    }

    setLoading(true)
    setError('')
    setData([])

    try {
      const from = format(fromDate, 'yyyy-MM-dd')
      const to = format(toDate, 'yyyy-MM-dd')
      
      const response = await api.get(`/accounts/balance-history?from=${from}&to=${to}`)
      
      if (response.data.success) {
        setData(response.data.data || [])
        if (response.data.data?.length === 0) {
          setError('No hay datos de saldos para el período seleccionado. Los snapshots se guardan automáticamente a las 00:00 y 12:00.')
        }
      } else {
        setError(response.data.error || 'Error al consultar el historial')
      }
    } catch (err) {
      console.error('Error:', err)
      setError(err.response?.data?.error || 'Error al consultar el historial de saldos')
    } finally {
      setLoading(false)
    }
  }

  const toggleExpand = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const getSnapshotTypeLabel = (type) => {
    switch (type) {
      case 'midnight': return '00:00 hs'
      case 'noon': return '12:00 hs'
      case 'manual': return 'Manual'
      default: return type
    }
  }

  const exportCSV = () => {
    if (data.length === 0) return

    let csvContent = 'Fecha,Hora,Cuenta,CVU,Saldo\n'
    
    data.forEach(snapshot => {
      snapshot.accounts.forEach(acc => {
        csvContent += `${snapshot.date},${getSnapshotTypeLabel(snapshot.type)},${acc.account_number},${acc.cvu || '-'},${acc.balance}\n`
      })
    })

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `saldo_historico_${format(fromDate, 'yyyyMMdd')}_${format(toDate, 'yyyyMMdd')}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value)
  }

  return (
    <div className="saldo-historico-page">
      <div className="saldo-historico-card">
        <div className="saldo-historico-header">
          <h2>Saldo Histórico</h2>
          <p className="saldo-historico-description">
            Consultar historial de saldos guardados a las 00:00 y 12:00 de cada día.
          </p>
        </div>

        <div className="saldo-historico-filters">
          <div className="filter-group">
            <label>Desde</label>
            <DatePicker
              selected={fromDate}
              onChange={(date) => setFromDate(date)}
              dateFormat="dd/MM/yyyy"
              locale={es}
              placeholderText="Seleccionar fecha"
              className="date-picker-input"
              disabled={loading}
              maxDate={new Date()}
            />
          </div>
          <div className="filter-group">
            <label>Hasta</label>
            <DatePicker
              selected={toDate}
              onChange={(date) => setToDate(date)}
              dateFormat="dd/MM/yyyy"
              locale={es}
              placeholderText="Seleccionar fecha"
              className="date-picker-input"
              disabled={loading}
              maxDate={new Date()}
              minDate={fromDate}
            />
          </div>
          <button 
            className="btn-consultar"
            onClick={fetchBalanceHistory}
            disabled={loading || !fromDate || !toDate}
          >
            {loading ? 'Consultando...' : 'Consultar'}
          </button>
        </div>

        {error && (
          <div className="saldo-historico-error">
            {error}
          </div>
        )}

        {data.length > 0 && (
          <>
            <div className="saldo-historico-actions">
              <button className="btn-export" onClick={exportCSV}>
                Exportar CSV
              </button>
              <span className="results-count">
                {data.length} snapshot(s) encontrado(s)
              </span>
            </div>

            <div className="saldo-historico-results">
              {data.map((snapshot, idx) => {
                const key = `${snapshot.date}_${snapshot.type}`
                const isExpanded = expanded[key]
                
                return (
                  <div key={idx} className="snapshot-card">
                    <div 
                      className="snapshot-header"
                      onClick={() => toggleExpand(key)}
                    >
                      <div className="snapshot-info">
                        <span className="snapshot-date">
                          {format(new Date(snapshot.date + 'T12:00:00'), 'dd/MM/yyyy', { locale: es })}
                        </span>
                        <span className={`snapshot-type ${snapshot.type}`}>
                          {getSnapshotTypeLabel(snapshot.type)}
                        </span>
                      </div>
                      <div className="snapshot-summary">
                        <span className="snapshot-accounts-count">
                          {snapshot.accounts.length} cuenta(s)
                        </span>
                        <span className="snapshot-total">
                          {formatCurrency(snapshot.total)}
                        </span>
                        <span className="expand-icon">{isExpanded ? '▲' : '▼'}</span>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="snapshot-details">
                        <table className="snapshot-table">
                          <thead>
                            <tr>
                              <th>Cuenta</th>
                              <th>CVU</th>
                              <th>Saldo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {snapshot.accounts.map((acc, accIdx) => (
                              <tr key={accIdx}>
                                <td>{acc.account_number}</td>
                                <td className="cvu-cell">{acc.cvu || '-'}</td>
                                <td className="amount-cell">
                                  {formatCurrency(acc.balance)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
